import { useMemo } from "react";

export interface TrendPoint {
  key: string;
  label: string;
  revenus: number;
  depenses: number;
}

interface RevenueTrendChartProps {
  data: TrendPoint[];
}

export default function RevenueTrendChart({ data }: RevenueTrendChartProps) {
  const max = useMemo(() => data.reduce((acc, point) => Math.max(acc, point.revenus, point.depenses), 0), [data]);
  const hasValues = max > 0;

  if (data.length === 0) {
    return (
      <div className="flex h-56 items-center justify-center rounded-lg border border-dashed border-background-300 text-sm text-foreground-500">
        Aucune période à afficher.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-4">
        <span className="flex items-center gap-2 text-xs text-foreground-600">
          <span className="h-2.5 w-2.5 rounded-full bg-primary-500" aria-hidden="true" />
          Revenus encaissés
        </span>
        <span className="flex items-center gap-2 text-xs text-foreground-600">
          <span className="h-2.5 w-2.5 rounded-full bg-accent-500" aria-hidden="true" />
          Dépenses
        </span>
      </div>

      <div className="relative flex h-56 items-end gap-2 overflow-x-auto pb-1">
        {data.map((point) => {
          const revenusPct = hasValues ? Math.round((point.revenus / max) * 100) : 0;
          const depensesPct = hasValues ? Math.round((point.depenses / max) * 100) : 0;
          return (
            <div key={point.key} className="flex min-w-[44px] flex-1 flex-col items-center gap-2">
              <div className="flex h-44 w-full items-end justify-center gap-1">
                <div
                  className="w-3 rounded-t bg-primary-500 transition-all duration-500"
                  style={{ height: `${revenusPct}%`, minHeight: point.revenus > 0 ? "4px" : "0" }}
                  title={`Revenus : ${point.revenus.toLocaleString("fr-FR")} FCFA`}
                  aria-hidden="true"
                />
                <div
                  className="w-3 rounded-t bg-accent-500 transition-all duration-500"
                  style={{ height: `${depensesPct}%`, minHeight: point.depenses > 0 ? "4px" : "0" }}
                  title={`Dépenses : ${point.depenses.toLocaleString("fr-FR")} FCFA`}
                  aria-hidden="true"
                />
              </div>
              <span className="whitespace-nowrap text-[10px] text-foreground-500">{point.label}</span>
            </div>
          );
        })}
      </div>

      {!hasValues && (
        <p className="text-center text-xs text-foreground-400">
          Aucun revenu ni dépense sur la période sélectionnée.
        </p>
      )}
    </div>
  );
}